/**
 * How It Works component - Left/right layout showing workflow
 * Left: Text explanations, Right: Product demo preview
 */

import {useState} from 'react';
import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

const steps = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    title: 'Start Your Meeting',
    description: 'Connect VoxaPilot to your favorite meeting platform. Real-time transcription begins automatically as soon as your meeting starts.',
    detail: 'Our intelligent system captures every word with speaker identification, ensuring nothing is missed.',
  },
  {
    id: 'transcript',
    label: 'Transcript',
    title: 'Get Instant Transcripts',
    description: 'Watch as your meeting is transcribed in real-time with timestamps and speaker labels. Search through conversations instantly.',
    detail: 'Every conversation is indexed and searchable, making it easy to find specific moments or topics.',
  },
  {
    id: 'summary',
    label: 'Summary',
    title: 'AI-Powered Insights',
    description: 'Automatically receive meeting summaries, action items, and key insights. Never miss important decisions or follow-ups.',
    detail: 'Our AI extracts the most important information, saving you hours of review time.',
  },
];

export function HowItWorks() {
  const {ref, isVisible} = useScrollAnimation();
  const [activeStep, setActiveStep] = useState(steps[0].id);

  const activeStepData = steps.find((s) => s.id === activeStep) || steps[0];

  return (
    <Section id="how-it-works" padding="lg" background="gray" ref={ref}>
      <Container>
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Header */}
          <div className="text-left mb-12">
            <p className="text-sm font-medium text-orange-600 dark:text-orange-500 mb-3 uppercase tracking-wide">
              How It Works
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Your Meetings, Transformed Into Understanding
            </h2>
            <p className="text-lg sm:text-xl text-textSecondary dark:text-[#858585] max-w-2xl">
              A simple three-step process that turns every meeting into actionable intelligence
            </p>
          </div>

          {/* Step Indicators */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-lg border border-border dark:border-gray-700 bg-background dark:bg-[#2D2D30] p-1">
              {steps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`
                    px-6 py-2.5 rounded-md text-base font-medium transition-all duration-300
                    ${
                      activeStep === step.id
                        ? 'bg-[#1E1E1E] dark:bg-[#1E1E1E] text-white shadow-sm scale-105'
                        : 'text-textSecondary dark:text-[#858585] hover:text-textPrimary dark:hover:text-[#CCCCCC] hover:scale-105'
                    }
                  `}
                >
                  {step.label}
                </button>
              ))}
            </div>
          </div>

          {/* Left/Right Layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Explanations */}
            <div className="space-y-6">
              <div
                key={activeStep}
                className="animate-fade-in transition-opacity duration-300"
              >
                <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
                  <h3 className="text-2xl sm:text-3xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
                    {activeStepData.title}
                  </h3>
                  <p className="text-lg text-textSecondary dark:text-[#858585] leading-relaxed mb-6">
                    {activeStepData.description}
                  </p>
                  <p className="text-base text-textTertiary dark:text-[#6A6A6A] leading-relaxed">
                    {activeStepData.detail}
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Product Demo Preview */}
            <div className="relative">
              <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden">
                {/* Browser Chrome */}
                <div className="bg-gray-100 dark:bg-[#252526] border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 bg-white dark:bg-[#1E1E1E] rounded-md px-4 py-1.5 mx-4 border border-gray-200 dark:border-gray-700">
                    <div className="text-xs text-textTertiary dark:text-[#6A6A6A]">app.voxapilot.com</div>
                  </div>
                </div>

                {/* Demo Content */}
                <div className="p-6 lg:p-8 bg-white dark:bg-[#1E1E1E]">
                  <div
                    key={activeStep}
                    className="animate-fade-in transition-opacity duration-300"
                  >
                    {activeStep === 'dashboard' ? (
                      <DashboardPreview />
                    ) : activeStep === 'transcript' ? (
                      <TranscriptPreview />
                    ) : (
                      <SummaryPreview />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}

// Preview Components
function DashboardPreview() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC]">Dashboard</h4>
        <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
          Live
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          {label: 'Meetings', value: '12'},
          {label: 'Hours', value: '8.2h'},
          {label: 'Items', value: '24'},
        ].map((stat) => (
          <div key={stat.label} className="bg-gray-50 dark:bg-[#252526] rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <div className="text-xl font-bold text-textPrimary dark:text-[#CCCCCC]">{stat.value}</div>
            <div className="text-xs text-textSecondary dark:text-[#858585]">{stat.label}</div>
          </div>
        ))}
      </div>
      <div className="bg-gray-50 dark:bg-[#252526] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">Recent Meeting</div>
        <div className="text-xs text-textSecondary dark:text-[#858585]">Q4 Planning • 45 min • 3 participants</div>
      </div>
    </div>
  );
}

function TranscriptPreview() {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC]">Transcript</h4>
        <button className="px-3 py-1 bg-orange-600 dark:bg-orange-500 text-white rounded-lg text-xs font-medium">
          Search
        </button>
      </div>
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {[
          {speaker: 'Sarah', text: "Let's kick off our Q4 planning session..."},
          {speaker: 'John', text: "I've prepared the sales targets document..."},
          {speaker: 'Mike', text: "We're on track to launch in January..."},
        ].map((entry, i) => (
          <div key={i} className="flex gap-3 p-3 bg-gray-50 dark:bg-[#252526] rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 bg-[#1E1E1E] dark:bg-[#1E1E1E] rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 border border-gray-200 dark:border-gray-700">
              {entry.speaker[0]}
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-textPrimary dark:text-[#CCCCCC] mb-1">{entry.speaker}</div>
              <div className="text-xs text-textSecondary dark:text-[#858585]">{entry.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryPreview() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-[#1E1E1E] dark:bg-[#1E1E1E] rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-700">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <h4 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC]">AI Summary</h4>
      </div>
      <div className="bg-gray-50 dark:bg-[#252526] rounded-lg p-4 border border-gray-200 dark:border-gray-700 space-y-3">
        <div>
          <div className="text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-1">Key Decisions</div>
          <div className="text-xs text-textSecondary dark:text-[#858585]">• Launch feature in January</div>
          <div className="text-xs text-textSecondary dark:text-[#858585]">• Increase marketing budget by 20%</div>
        </div>
        <div>
          <div className="text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-1">Action Items</div>
          <div className="text-xs text-textSecondary dark:text-[#858585]">✓ 4 items identified</div>
        </div>
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-textTertiary dark:text-[#6A6A6A]">
          <span>3 participants</span>
          <span>45 minutes</span>
        </div>
      </div>
    </div>
  );
}

